import type {
    PlanCardStep,
    PlannerParseResult,
    PlannerPlan,
    PlannerStep,
} from '../components/planner-mode/types';
import type {
    ChatMessageContent,
    PlanTemplateStepOption,
    PlanTemplateStep,
    PlanTemplateStepType,
} from '../types';

function extractTextContent(content: string | ChatMessageContent[]): string {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
        return content
            .filter((c) => c.type === 'text' && c.text)
            .map((c) => c.text)
            .join('');
    }
    return '';
}

/**
 * Walks a string counting balanced braces (respecting JSON string literals)
 * and returns each top-level `{...}` substring along with the text that
 * precedes the first JSON object.
 */
function extractTopLevelJsonObjects(text: string): {
    precedingText: string;
    jsonStrings: string[];
} {
    const jsonStrings: string[] = [];
    let precedingText = '';
    let foundFirst = false;
    let i = 0;

    while (i < text.length) {
        if (text[i] === '{') {
            if (!foundFirst) {
                precedingText = text.slice(0, i).trim();
                foundFirst = true;
            }
            let depth = 0;
            const start = i;
            let inString = false;
            let escape = false;

            while (i < text.length) {
                const ch = text[i];
                if (escape) {
                    escape = false;
                } else if (ch === '\\' && inString) {
                    escape = true;
                } else if (ch === '"') {
                    inString = !inString;
                } else if (!inString) {
                    if (ch === '{') depth++;
                    else if (ch === '}') {
                        depth--;
                        if (depth === 0) {
                            jsonStrings.push(text.slice(start, i + 1));
                            i++;
                            break;
                        }
                    }
                }
                i++;
            }
        } else {
            i++;
        }
    }

    if (!foundFirst) precedingText = text.trim();
    return { precedingText, jsonStrings };
}

// ---------------------------------------------------------------------------
// Config format types (DANI_PLAN_BUILD_CONFIG)
// ---------------------------------------------------------------------------

interface ConfigPlanPath {
    id: string;
    label: string;
    title: string;
    description: string;
    cta: string;
}

interface ConfigDataInput {
    field: string;
    label: string;
    type: string;
    current_value: string | string[];
    options?: string[];
}

interface ConfigVertical {
    id: string;
    label: string;
    selected: boolean;
}

interface ConfigModule {
    id: string;
    label: string;
    description: string;
    enabled: boolean;
    locked: boolean;
}

interface DaniPlanConfig {
    goal_summary: string;
    assumption_note?: string;
    brand_name?: string;
    plan_type_input?: string;
    message?: string;
    plan_paths: ConfigPlanPath[];
    data_inputs: ConfigDataInput[];
    suggested_verticals?: ConfigVertical[];
    report_modules: ConfigModule[];
    suggested_modules?: ConfigModule[];
}

// ---------------------------------------------------------------------------
// Normalizer: DaniPlanConfig → PlannerPlan
// ---------------------------------------------------------------------------

function normalizeConfigToPlan(config: DaniPlanConfig, runId: string): PlannerPlan {
    const steps: PlannerStep[] = [];
    let stepNum = 1;

    if (config.suggested_verticals && config.suggested_verticals.length > 0) {
        steps.push({
            step: stepNum++,
            label: 'Verticals',
            fields: [
                {
                    type: 'multi_tag',
                    key: 'verticals',
                    label: 'Industry Verticals',
                    values: config.suggested_verticals
                        .filter((v) => v.selected)
                        .map((v) => v.label),
                    suggested: config.suggested_verticals.map((v) => v.label),
                },
            ],
        });
    }

    if (config.data_inputs && config.data_inputs.length > 0) {
        steps.push({
            step: stepNum++,
            label: 'Data Inputs',
            fields: config.data_inputs.map((input) => ({
                type: input.type as 'multi_tag' | 'dropdown' | 'text',
                key: input.field,
                label: input.label,
                values: Array.isArray(input.current_value) ? input.current_value : undefined,
                value: typeof input.current_value === 'string' ? input.current_value : undefined,
                options: input.options?.map((o) => ({ value: o, label: o })),
            })),
        });
    }

    if (
        (config.report_modules && config.report_modules.length > 0) ||
        (config.suggested_modules && config.suggested_modules.length > 0)
    ) {
        steps.push({
            step: stepNum++,
            label: 'Report Modules',
            modules: config.report_modules?.map((m) => ({
                id: m.id,
                name: m.label,
                description: m.description,
                enabled: m.enabled,
                locked: m.locked,
            })),
            suggested_modules: config.suggested_modules?.map((m) => ({
                id: m.id,
                name: m.label,
                description: m.description,
                enabled: m.enabled,
                locked: m.locked,
            })),
        });
    }

    if (config.plan_paths && config.plan_paths.length > 0) {
        steps.push({
            step: stepNum++,
            label: 'Generate Report',
            paths: config.plan_paths.map((p, i) => ({
                id: p.id,
                label: p.title || p.label,
                description: p.description,
                recommended: i === 0 || p.label?.toLowerCase() === 'recommended',
                cta: p.cta,
            })),
        });
    }

    return {
        type: 'plan_card',
        run_id: runId,
        brand_name: config.brand_name ?? '',
        title:
            config.goal_summary ||
            `${config.brand_name ?? ''} – ${config.plan_type_input ?? ''}`.trim(),
        assumption_note: config.assumption_note,
        steps,
    };
}

// ---------------------------------------------------------------------------
// Extract config from tool_result items or text content
// ---------------------------------------------------------------------------

function tryExtractDaniConfig(content: string | ChatMessageContent[]): DaniPlanConfig | null {
    // Try tool_result items first (more structured)
    if (Array.isArray(content)) {
        for (const item of content) {
            const raw = item as unknown as Record<string, unknown>;
            if (raw.type === 'tool_result' && raw.tool_result) {
                const toolResult = raw.tool_result as Record<string, unknown>;
                if (
                    toolResult.name === 'DANI_PLAN_BUILD_CONFIG' &&
                    Array.isArray(toolResult.content)
                ) {
                    for (const resultItem of toolResult.content as Record<string, unknown>[]) {
                        if (resultItem.type === 'json') {
                            const jsonPayload = resultItem.json as Record<string, unknown>;
                            if (typeof jsonPayload?.result === 'string') {
                                try {
                                    const parsed = JSON.parse(jsonPayload.result);
                                    const config = parsed?.config ?? parsed;
                                    if (config.goal_summary && config.plan_paths) {
                                        if (!config.brand_name && parsed.brand_name) {
                                            config.brand_name = parsed.brand_name;
                                        }
                                        return config as DaniPlanConfig;
                                    }
                                } catch {
                                    /* not valid JSON */
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Fall back to finding config JSON in the text content
    const textContent = extractTextContent(content);
    if (!textContent) return null;

    if (!textContent.includes('"goal_summary"') || !textContent.includes('"plan_paths"')) {
        return null;
    }

    const jsonStart = textContent.indexOf('{');
    if (jsonStart === -1) return null;

    try {
        const parsed = JSON.parse(textContent.slice(jsonStart).trim());
        const config = parsed?.config ?? parsed;
        if (config.goal_summary && config.plan_paths) {
            if (!config.brand_name && parsed.brand_name) {
                config.brand_name = parsed.brand_name;
            }
            return config as DaniPlanConfig;
        }
    } catch {
        /* not valid JSON */
    }

    return null;
}

// ---------------------------------------------------------------------------
// Inline widget types (e.g. choice_cards, multi_tag)
// ---------------------------------------------------------------------------

interface InlineWidgetOption {
    id: string;
    label: string;
    description: string;
}

interface InlineWidget {
    type: 'inline_widget';
    widget: string;
    label: string;
    run_id?: string;
    options: InlineWidgetOption[];
}

interface InlineWidgetField {
    type: string;
    key: string;
    label: string;
    values?: string[];
    value?: string;
    options?: (string | { value: string; label: string })[];
    suggested?: string[];
    allow_add?: boolean;
    allow_custom?: boolean;
}

interface InlineFieldWidget {
    type: 'inline_widget';
    widget: string;
    label: string;
    fields: InlineWidgetField[];
}

function normalizeInlineWidgetToPlan(widget: InlineWidget): PlannerPlan {
    const step: PlannerStep = {
        step: 1,
        label: widget.label,
        options: widget.options,
    };

    return {
        type: 'inline_widget',
        run_id: widget.run_id || '',
        brand_name: '',
        title: widget.label,
        steps: [step],
        stepMode: {
            currentStep: 1,
            totalSteps: 1,
            isLast: false,
        },
    };
}

function normalizeFieldWidgetToPlan(widget: InlineFieldWidget): PlannerPlan {
    const step: PlannerStep = {
        step: 1,
        label: widget.label,
        fields: widget.fields.map((f) => ({
            type: f.type as 'multi_tag' | 'dropdown' | 'text',
            key: f.key,
            label: f.label,
            values: f.values,
            value: f.value,
            suggested: f.suggested,
            allow_add: f.allow_add,
            allow_custom: f.allow_custom,
            options: f.options?.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
        })),
    };

    return {
        type: 'inline_widget',
        run_id: '',
        brand_name: '',
        title: widget.label,
        steps: [step],
        stepMode: {
            currentStep: 1,
            totalSteps: 1,
            isLast: false,
        },
    };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Attempts to parse a planner plan from message content.
 *
 * Supports two formats:
 *   1. Legacy plan_card: `{"type": "plan_card", "plan": {...}}`
 *   2. DANI_PLAN_BUILD_CONFIG: tool_result or text JSON with `goal_summary`,
 *      `plan_paths`, `data_inputs`, etc.
 *
 * @param metadata - Optional message metadata; `run_id` is extracted for the
 *                   config format where run_id lives outside the plan JSON.
 */
/**
 * Normalizes a `PlanCardStep` (single-step wizard JSON) into a `PlannerPlan`
 * with one step and `stepMode` metadata, so `PlannerCard` can render both
 * single-step and multi-step plans uniformly.
 */
function normalizeStepCardToPlan(stepCard: PlanCardStep): PlannerPlan {
    const step: PlannerStep = {
        step: stepCard.step.step,
        id: stepCard.step.id,
        label: stepCard.step.label,
        search_hint: stepCard.step.search_hint,
        fields: stepCard.step.fields,
        modules: stepCard.step.modules,
        suggested_modules: stepCard.step.suggested_modules,
        paths: stepCard.step.paths,
        options: stepCard.step.options,
    };

    return {
        type: 'plan_card_step',
        run_id: stepCard.plan_id,
        brand_name: stepCard.brand_name,
        title: stepCard.title,
        steps: [step],
        stepMode: {
            commentary: stepCard.commentary,
            currentStep: stepCard.step.step,
            totalSteps: stepCard.total_steps,
            isLast: stepCard.is_last,
        },
    };
}

/**
 * Extracts ALL planner cards (both plan_card_step and plan_card) from a single
 * message. A message may contain multiple JSON objects — e.g. a step card
 * followed by a full plan card. Returns them all so the UI can render each.
 */
export function parseAllPlannerCards(
    content: string | ChatMessageContent[],
    metadata?: Record<string, unknown>,
): PlannerParseResult[] {
    const results: PlannerParseResult[] = [];
    const textContent = extractTextContent(content);

    if (textContent) {
        const { precedingText, jsonStrings } = extractTopLevelJsonObjects(textContent);

        for (const jsonStr of jsonStrings) {
            try {
                const parsed = JSON.parse(jsonStr);

                console.log(
                    '[parseAllPlannerCards] parsed JSON type:',
                    parsed?.type,
                    '| step.id:',
                    parsed?.step?.id,
                    '| step keys:',
                    parsed?.step ? Object.keys(parsed.step) : 'n/a',
                );

                if (parsed?.type === 'plan_card') {
                    const plan = (parsed.plan ?? parsed) as PlannerPlan;
                    if (plan.title && Array.isArray(plan.steps)) {
                        results.push({
                            text: results.length === 0 ? precedingText : '',
                            plan,
                        });
                        continue;
                    }
                }

                if (
                    parsed?.type === 'plan_card_step' &&
                    parsed.step &&
                    (parsed.step.id ||
                        Array.isArray(parsed.step.options) ||
                        Array.isArray(parsed.step.fields) ||
                        Array.isArray(parsed.step.modules) ||
                        Array.isArray(parsed.step.paths))
                ) {
                    const plan = normalizeStepCardToPlan(parsed as PlanCardStep);
                    results.push({
                        text: results.length === 0 ? precedingText : '',
                        plan,
                    });
                    continue;
                }

                if (parsed?.type === 'inline_widget' && parsed.widget) {
                    let plan: PlannerPlan | null = null;
                    if (Array.isArray(parsed.options)) {
                        plan = normalizeInlineWidgetToPlan(parsed as InlineWidget);
                    } else if (Array.isArray(parsed.fields)) {
                        plan = normalizeFieldWidgetToPlan(parsed as InlineFieldWidget);
                    }
                    if (plan) {
                        results.push({
                            text: results.length === 0 ? precedingText : '',
                            plan,
                        });
                        continue;
                    }
                }
            } catch {
                /* not valid JSON */
            }
        }
    }

    // Fall back to DANI_PLAN_BUILD_CONFIG format
    if (results.length === 0) {
        const config = tryExtractDaniConfig(content);
        if (config) {
            const runId = metadata?.run_id ? String(metadata.run_id) : '';
            const plan = normalizeConfigToPlan(config, runId);
            results.push({ text: config.message || '', plan });
        }
    }

    return results;
}

/**
 * Returns true if the content appears to contain a partial or complete
 * planner JSON blob. Used to suppress raw JSON while streaming.
 */
export function looksLikePlannerJson(content: string | ChatMessageContent[]): boolean {
    const textContent = extractTextContent(content);

    // Legacy plan_card format
    if (textContent.includes('"plan_card"') || textContent.includes('"plan_card",')) {
        return true;
    }

    // plan_card_step format
    if (textContent.includes('"plan_card_step"')) {
        return true;
    }

    // inline_widget format (e.g. choice_cards)
    if (textContent.includes('"inline_widget"') && textContent.includes('"widget"')) {
        return true;
    }

    // Config format – detectable from text content
    if (textContent.includes('"goal_summary"') && textContent.includes('"plan_paths"')) {
        return true;
    }

    // Config format – detectable from tool_use item in content array
    if (Array.isArray(content)) {
        for (const item of content) {
            const raw = item as unknown as Record<string, unknown>;
            if (raw.type === 'tool_use') {
                const toolUse = raw.tool_use as Record<string, unknown> | undefined;
                if (toolUse?.name === 'DANI_PLAN_BUILD_CONFIG') return true;
            }
        }
    }

    return false;
}

/**
 * Plan Mode Step Config Utils
 */

export function slugify(value: string): string {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

export function createEmptyStep(existingIds: string[]): PlanTemplateStep {
    const base = 'step';
    let i = existingIds.length + 1;
    let candidate = `${base}_${i}`;
    while (existingIds.includes(candidate)) {
        i += 1;
        candidate = `${base}_${i}`;
    }
    return {
        id: candidate,
        question: '',
        type: 'text',
        allow_skip: false,
    };
}

export function createEmptyOption(existingIds: string[]): PlanTemplateStepOption {
    let i = existingIds.length + 1;
    let candidate = `option_${i}`;
    while (existingIds.includes(candidate)) {
        i += 1;
        candidate = `option_${i}`;
    }
    return { id: candidate, label: '' };
}

export function supportsOptions(type: PlanTemplateStepType): boolean {
    return type === 'select' || type === 'multi_select';
}

export function supportsPlaceholder(type: PlanTemplateStepType): boolean {
    return type === 'text' || type === 'text_list';
}
